import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError, apiFetch } from '@/lib/api';
import { isDemoModeEnabled } from '@/lib/demo';
import { useAuthStore } from '@/stores/auth-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { CreateStaffInput } from '@tunodo/ticketing-shared';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

export const Route = createFileRoute('/_app/admin/staff')({
  beforeLoad: () => {
    const { user } = useAuthStore.getState();
    if (user?.role !== 'ORGANIZER') {
      toast.info('Solo los organizadores pueden gestionar el staff.');
      throw redirect({ to: '/' });
    }
  },
  component: StaffPage,
});

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------
const StaffDto = z.object({
  id: z.string(),
  email: z.string(),
  fullName: z.string(),
  role: z.string(),
  createdAt: z.string(),
});
type StaffDtoType = z.infer<typeof StaffDto>;

const StaffListSchema = z.array(StaffDto);

const ROLE_LABELS: Record<string, string> = {
  ORGANIZER: 'Organizador',
  STAFF_ATTENDANCE: 'Staff Asistencia',
  STAFF_LUNCH: 'Staff Almuerzo',
  STAFF_SCANNER: 'Staff Scanner',
};

const ROLE_OPTIONS = [
  { value: 'ORGANIZER', label: 'Organizador' },
  { value: 'STAFF_ATTENDANCE', label: 'Staff Asistencia' },
  { value: 'STAFF_LUNCH', label: 'Staff Almuerzo' },
  { value: 'STAFF_SCANNER', label: 'Staff Scanner' },
] as const;

function roleBadgeVariant(role: string): 'default' | 'secondary' | 'outline' {
  if (role === 'ORGANIZER') return 'default';
  if (role === 'STAFF_SCANNER') return 'secondary';
  return 'outline';
}

// ---------------------------------------------------------------------------
// Edit name dialog
// ---------------------------------------------------------------------------
function EditNameDialog({
  staff,
  open,
  onOpenChange,
}: {
  staff: StaffDtoType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [fullName, setFullName] = useState(staff.fullName);

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch(`/api/staff/${staff.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ fullName: fullName.trim() }),
        schema: StaffDto,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff'] });
      onOpenChange(false);
      toast.success('Nombre actualizado.');
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Error al actualizar nombre');
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Editar nombre</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="staff-fullname">Nombre completo</Label>
            <Input
              id="staff-fullname"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              disabled={mutation.isPending}
              placeholder="Nombre completo"
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" size="sm">
              Cancelar
            </Button>
          </DialogClose>
          <Button
            size="sm"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !fullName.trim()}
          >
            {mutation.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Create staff dialog
// ---------------------------------------------------------------------------
function CreateStaffDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<string>('STAFF_ATTENDANCE');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: { email: string; fullName: string; role: string; password: string }) =>
      apiFetch('/api/staff', {
        method: 'POST',
        body: JSON.stringify(body),
        schema: StaffDto,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff'] });
      toast.success('Staff creado correctamente.');
      onOpenChange(false);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.error('Email ya registrado');
      } else {
        toast.error(err instanceof ApiError ? err.message : 'Error al crear staff');
      }
    },
  });

  function handleSubmit() {
    setValidationError(null);

    if (password !== confirmPassword) {
      setValidationError('Las contraseñas no coinciden');
      return;
    }

    const result = CreateStaffInput.safeParse({ email, fullName, role, password });
    if (!result.success) {
      const first = result.error.errors[0];
      setValidationError(first?.message ?? 'Datos inválidos');
      return;
    }

    mutation.mutate(result.data);
  }

  function handleOpenChange(next: boolean) {
    if (!next) {
      setEmail('');
      setFullName('');
      setRole('STAFF_ATTENDANCE');
      setPassword('');
      setConfirmPassword('');
      setValidationError(null);
    }
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Crear staff</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="create-staff-email">Email</Label>
            <Input
              id="create-staff-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={mutation.isPending}
              placeholder="correo@ejemplo.com"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="create-staff-fullname">Nombre completo</Label>
            <Input
              id="create-staff-fullname"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              disabled={mutation.isPending}
              placeholder="Nombre completo"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="create-staff-role">Rol</Label>
            <Select value={role} onValueChange={setRole} disabled={mutation.isPending}>
              <SelectTrigger id="create-staff-role">
                <SelectValue placeholder="Selecciona un rol" />
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="create-staff-password">Contraseña</Label>
            <Input
              id="create-staff-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={mutation.isPending}
              placeholder="Mínimo 8 caracteres"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="create-staff-confirm">Confirmar contraseña</Label>
            <Input
              id="create-staff-confirm"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={mutation.isPending}
              placeholder="Repite la contraseña"
            />
          </div>
          {validationError && <p className="text-destructive text-sm">{validationError}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" size="sm" disabled={mutation.isPending}>
              Cancelar
            </Button>
          </DialogClose>
          <Button size="sm" onClick={handleSubmit} disabled={mutation.isPending}>
            {mutation.isPending ? 'Creando…' : 'Crear'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
function StaffPage() {
  const currentUser = useAuthStore((s) => s.user);
  const [editingStaff, setEditingStaff] = useState<StaffDtoType | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const {
    data: staffList,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['staff'],
    queryFn: () => apiFetch('/api/staff', { schema: StaffListSchema }),
    staleTime: 30_000,
  });

  // Exclude current organizer from the list
  const filteredStaff = staffList?.filter((s) => s.id !== currentUser?.id);

  if (isLoading) {
    return (
      <main className="flex-1 p-6 space-y-6">
        <Skeleton className="h-8 w-40" />
        <div className="rounded-md border overflow-x-auto">
          <Table className="min-w-[480px]">
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 4 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows have no identity
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-4 w-48" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-40" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-8 w-16" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>
    );
  }

  if (isError) {
    return (
      <main className="flex-1 p-6">
        <p className="text-destructive text-sm">No se pudo cargar la lista de staff.</p>
      </main>
    );
  }

  return (
    <main className="flex-1 p-6 space-y-6">
      {isDemoModeEnabled() && currentUser?.role === 'ORGANIZER' && (
        <Alert className="border-amber-300 bg-amber-50 text-amber-900">
          <AlertTriangle className="h-4 w-4 !text-amber-900" />
          <AlertDescription>
            Modo demo activo. Los staff users con email @demo.local se borrarán en el próximo reset
            del evento demo.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Gestión de staff</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {filteredStaff?.length ?? 0} usuario{filteredStaff?.length !== 1 ? 's' : ''} de staff
          </p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          Crear staff
        </Button>
      </div>

      {!filteredStaff || filteredStaff.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          No hay usuarios de staff registrados.
        </p>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <Table className="min-w-[480px]">
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Nombre completo</TableHead>
                <TableHead className="w-20">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStaff.map((staff) => (
                <TableRow key={staff.id}>
                  <TableCell className="text-muted-foreground">{staff.email}</TableCell>
                  <TableCell>
                    <Badge variant={roleBadgeVariant(staff.role)}>
                      {ROLE_LABELS[staff.role] ?? staff.role}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">{staff.fullName}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => setEditingStaff(staff)}>
                      Editar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {editingStaff && (
        <EditNameDialog
          staff={editingStaff}
          open={!!editingStaff}
          onOpenChange={(open) => {
            if (!open) setEditingStaff(null);
          }}
        />
      )}

      <CreateStaffDialog open={createOpen} onOpenChange={setCreateOpen} />
    </main>
  );
}
